import { useState, useEffect, useRef } from 'react';
import { useLobsterStore } from '../store/useLobsterStore';
import { Claw3DView } from './Claw3DView';
import { ApprovalCard } from './ApprovalCard';
import type { Message } from '../types/message';
import type { Device } from '../hooks/useDeviceDiscovery';
import './ChatView.css';

interface ChatViewProps {
  device: Device;
  sendMessage: (text: string, accountId: string) => Promise<boolean>;
  respondApproval: (requestId: string, decision: 'approved' | 'rejected') => Promise<boolean>;
  onDisconnect: () => void;
  username: string;
  onLogout: () => void;
}

function getStatusText(status: string, task: string): string {
  if (status === 'working' || status === 'busy' || status === 'thinking') {
    return task || '工作中...';
  }
  const map: Record<string, string> = {
    online: '准备就绪', idle: '休息中', offline: '已离线', error: '遇到问题',
  };
  return map[status] || task || '状态未知';
}

const statusColors: Record<string, string> = {
  online: '#00B894', working: '#FF9F43', thinking: '#A78BFA',
  busy: '#E17055', idle: '#FDCB6E', offline: '#B2BEC3', error: '#E17055', connecting: '#FDCB6E',
};

export function ChatView({ device, sendMessage, respondApproval, onDisconnect, username, onLogout }: ChatViewProps) {
  const { agents, activeAgentId, setActiveAgent, connection, messagesByAgent, addMessages, updateApproval } = useLobsterStore();

  const currentAgent = agents.find(a => a.id === activeAgentId) || agents[0];
  const agentName = currentAgent?.name || '小龙虾';
  const agentAccountId = currentAgent?.accountId || currentAgent?.id || '';
  const status = currentAgent?.status || 'offline';
  const task = currentAgent?.currentTask || '';

  const messages = messagesByAgent[agentAccountId] || [];

  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reconnectFlash, setReconnectFlash] = useState(false);
  const prevConnectionRef = useRef(connection);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Track connection transitions for reconnect flash
  useEffect(() => {
    if (prevConnectionRef.current === 'offline' && connection === 'online') {
      setReconnectFlash(true);
      const timer = setTimeout(() => setReconnectFlash(false), 2000);
      return () => clearTimeout(timer);
    }
    prevConnectionRef.current = connection;
  }, [connection]);

  const handleApprovalResponse = async (requestId: string, decision: 'approved' | 'rejected'): Promise<boolean> => {
    const ok = await respondApproval(requestId, decision);
    if (ok) {
      updateApproval(requestId, decision);
    }
    return ok;
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputText.trim() || sending || !agentAccountId) return;
    const text = inputText.trim();
    setSending(true);
    setInputText('');

    // Optimistic update
    addMessages(agentAccountId, [{
      messageId: `local-${Date.now()}`,
      accountId: agentAccountId,
      from: 'user',
      text,
      timestamp: Date.now(),
      type: 'text',
    }]);

    await sendMessage(text, agentAccountId);
    setSending(false);
  };

  return (
    <div className="chat-shell">
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-left">
          <button className="chat-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
            <span /><span /><span />
          </button>
          <span className="chat-brand">Lobster</span>
        </div>
        <div className="chat-conn">
          <div className="chat-conn-dot" style={{ background: connection === 'online' ? '#00B894' : '#B2BEC3' }} />
          <span>{device.machine_name || 'PC'}</span>
        </div>
      </div>

      {/* Menu overlay */}
      {menuOpen && (
        <>
          <div className="chat-menu-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="chat-menu">
            <div className="chat-menu-user">{username}</div>
            <div className="chat-menu-item" onClick={() => { onDisconnect(); setMenuOpen(false); }}>
              切换设备
            </div>
            <div className="chat-menu-item" onClick={onLogout}>
              退出登录
            </div>
          </div>
        </>
      )}

      {/* Connection status banner */}
      {connection === 'offline' && (
        <div className="chat-conn-banner offline">
          连接已断开，正在重连...
        </div>
      )}
      {reconnectFlash && connection === 'online' && (
        <div className="chat-conn-banner online">
          已重新连接
        </div>
      )}

      {/* Agent switcher */}
      {agents.length > 1 && (
        <div className="chat-agents">
          {agents.map((agent) => (
            <button
              key={agent.id}
              className={`chat-agent-tab ${agent.id === activeAgentId ? 'active' : ''}`}
              onClick={() => setActiveAgent(agent.id)}
            >
              <span className="chat-agent-dot" style={{ background: statusColors[agent.status] || '#B2BEC3' }} />
              {agent.name}
            </button>
          ))}
        </div>
      )}

      {/* 3D Character + Status */}
      <div className="chat-scene-area">
        <Claw3DView status={status} />
        <div className="chat-status-overlay">
          <span className="chat-status-name">{agentName}</span>
          <span className="chat-status-text">{getStatusText(status, task)}</span>
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">和 {agentName} 说点什么吧...</div>
        )}
        {messages.map((msg) => (
          msg.type === 'approval' && msg.approval ? (
            <div key={msg.messageId} className="chat-msg-row agent">
              <div className="chat-msg-avatar">&#x1F99E;</div>
              <ApprovalCard approval={msg.approval} onRespond={handleApprovalResponse} />
            </div>
          ) : (
            <MessageBubble key={msg.messageId} message={msg} agentName={agentName} />
          )
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-bar">
        <input
          type="text"
          className="chat-input"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder={`和 ${agentName} 说点什么...`}
          disabled={sending || connection !== 'online'}
        />
        <button
          className={`chat-send-btn ${inputText.trim() && !sending ? 'active' : ''}`}
          onClick={handleSend}
          disabled={!inputText.trim() || sending}
        >
          {sending ? '...' : '发送'}
        </button>
      </div>
    </div>
  );
}

function MessageBubble({ message, agentName }: { message: Message; agentName: string }) {
  const isAgent = message.from === 'agent';
  const time = new Date(message.timestamp).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`chat-msg-row ${isAgent ? 'agent' : 'user'}`}>
      {isAgent && <div className="chat-msg-avatar">🦞</div>}
      <div className={`chat-msg-bubble ${isAgent ? 'agent' : 'user'}`}>
        {isAgent && <div className="chat-msg-sender">{agentName}</div>}
        <div>{message.text}</div>
        <div className={`chat-msg-time ${isAgent ? 'agent' : 'user'}`}>{time}</div>
      </div>
    </div>
  );
}
